<template>
  <HrPageLayout
    title="My Performance Appraisal"
    subtitle="Targets, progress, mid-year and end-year appraisal workflow"
    icon="📝"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'My Performance' }, { label: 'Appraisal' }]"
  >
    <div class="my">
      <div class="my-list" v-if="!selectedId">
        <div v-if="loading" class="muted pad">Loading…</div>
        <div v-else-if="!list.length" class="empty">
          <h5>No appraisal assignment</h5>
          <p>When the Head of Performance launches a cycle, your appraisal will appear here.</p>
        </div>
        <div v-else class="cards">
          <button v-for="a in list" :key="a.id" type="button" class="card" @click="open(a.id)">
            <strong>{{ a.cycle_name }}</strong>
            <span>{{ a.financial_year }} · {{ a.form_type === 'job_group_j_above' ? 'Form J+' : 'Form H−' }}</span>
            <em class="st">{{ formatStatus(a.status) }}</em>
            <small>Supervisor: {{ a.supervisor_name || 'Not assigned' }}</small>
          </button>
        </div>
      </div>

      <div v-else class="workspace">
        <div class="ws-head">
          <button class="hr-btn hr-btn-ghost" @click="selectedId=null; detail=null">← Back</button>
          <div>
            <h3>{{ detail?.assignment?.cycle_name }}</h3>
            <p>{{ formatStatus(detail?.assignment?.status) }} · Weight {{ weightTotal }}% · Scores: Emp {{ detail?.assignment?.endyear_emp_score ?? '—' }} / Sup {{ detail?.assignment?.endyear_sup_score ?? '—' }} / Agreed {{ detail?.assignment?.endyear_agreed_score ?? '—' }}</p>
          </div>
          <div class="tabs">
            <button v-for="t in tabs" :key="t.id" type="button" :class="{ active: tab===t.id }" @click="tab=t.id">{{ t.label }}</button>
          </div>
        </div>

        <div v-if="!detail" class="muted pad">Loading assignment…</div>
        <template v-else>
          <!-- Targets -->
          <div v-show="tab==='targets'" class="panel">
            <div class="flow-box">
              <strong>Target agreement workflow</strong>
              <ol>
                <li>You draft targets (weights must total 100%).</li>
                <li>Submit for supervisor review.</li>
                <li>Supervisor discusses and either <em>agrees &amp; approves</em> or returns for revision.</li>
                <li>Once approved, targets are locked for mid-year and end-year rating.</li>
              </ol>
              <p v-if="detail?.assignment?.status === 'awaiting_supervisor_review'" class="info">Submitted — waiting for supervisor agreement.</p>
              <p v-if="detail?.assignment?.status === 'targets_approved'" class="ok">Targets agreed with supervisor and locked.</p>
              <p v-if="detail?.assignment?.status === 'targets_rejected'" class="bad">Returned by supervisor: {{ detail?.assignment?.rejection_reason || 'revise and resubmit' }}</p>
            </div>
            <div v-if="formTemplate" class="form-box">
              <strong>Your form: {{ formTemplate.title }}</strong>
              <p>{{ formTemplate.guidance_text }}</p>
              <ul v-if="formTemplate.sections?.length">
                <li v-for="(s, i) in formTemplate.sections" :key="i"><b>{{ s.title }}</b> — {{ s.prompt }}</li>
              </ul>
              <small v-if="formTemplate.file_name">Reference document: {{ formTemplate.file_name }}</small>
            </div>
            <p class="hint">Total weight must equal <strong>100%</strong> before submit. Current: <strong :class="{ bad: Math.abs(weightTotal-100)>0.05 }">{{ weightTotal }}%</strong></p>
            <div v-for="(t, idx) in targets" :key="idx" class="tgt">
              <div class="tgt-h">Target {{ idx+1 }} <button v-if="canEditTargets" type="button" class="link danger" @click="targets.splice(idx,1)">Remove</button></div>
              <div class="g2">
                <label>KRA <input v-model="t.kra" :disabled="!canEditTargets"></label>
                <label>Weight % <input type="number" step="0.01" v-model.number="t.weight" :disabled="!canEditTargets"></label>
                <label class="full">Performance objective <textarea v-model="t.objective" rows="2" :disabled="!canEditTargets"></textarea></label>
                <label class="full">Activities / expected output <textarea v-model="t.activities" rows="2" :disabled="!canEditTargets"></textarea></label>
                <label>Indicator <input v-model="t.indicator" :disabled="!canEditTargets"></label>
                <label>Annual target <input v-model="t.annual_target" :disabled="!canEditTargets"></label>
                <label>Completion date <input type="date" v-model="t.completion_date" :disabled="!canEditTargets"></label>
                <label>Means of verification <input v-model="t.means_of_verification" :disabled="!canEditTargets"></label>
                <label class="full">Employee comments <textarea v-model="t.emp_comments" rows="2" :disabled="!canEditTargets"></textarea></label>
              </div>
            </div>
            <div class="actions" v-if="canEditTargets">
              <button class="hr-btn hr-btn-outline-primary" @click="addTarget">+ Target</button>
              <button class="hr-btn hr-btn-secondary" :disabled="saving" @click="saveTargets(true)">Save draft</button>
              <button class="hr-btn hr-btn-primary" :disabled="saving" @click="submitTargets">Submit for supervisor agreement</button>
            </div>
          </div>

          <!-- Progress -->
          <div v-show="tab==='progress'" class="panel">
            <p class="hint">Record progress without changing locked targets. Attach evidence under evidence section as needed.</p>
            <div v-for="t in targets" :key="'p'+t.id" class="tgt">
              <strong>{{ t.kra || t.objective || ('Target '+t.id) }}</strong>
              <label>Progress note <textarea v-model="progressForms[t.id].progress_note" rows="2"></textarea></label>
              <label>Actual achievement <textarea v-model="progressForms[t.id].actual_achievement" rows="2"></textarea></label>
              <label>Challenges <textarea v-model="progressForms[t.id].challenges" rows="2"></textarea></label>
              <label>Support required <input v-model="progressForms[t.id].support_required"></label>
              <button class="hr-btn hr-btn-sm hr-btn-primary" @click="saveProgress(t.id)">Save progress</button>
            </div>
          </div>

          <!-- Mid-year -->
          <div v-show="tab==='midyear'" class="panel">
            <div v-for="t in targets" :key="'m'+t.id" class="tgt">
              <strong>{{ t.kra || ('Target '+t.id) }}</strong>
              <div class="g2">
                <label class="full">Progress achieved <textarea v-model="t.mid_progress" rows="2"></textarea></label>
                <label>Appraisee rating (1-5) <input type="number" min="1" max="5" step="0.1" v-model.number="t.mid_emp_rating"></label>
                <label>Agreed rating <input type="number" min="1" max="5" step="0.1" v-model.number="t.mid_agreed_rating"></label>
                <label class="full">Employee comments <textarea v-model="t.mid_emp_comment" rows="2"></textarea></label>
                <label class="full">Constraints <textarea v-model="t.mid_constraints" rows="2"></textarea></label>
                <label class="full">Support required <textarea v-model="t.mid_support_required" rows="2"></textarea></label>
                <label class="full">Improvement actions <textarea v-model="t.mid_improvement_actions" rows="2"></textarea></label>
              </div>
            </div>
            <div class="actions">
              <button class="hr-btn hr-btn-primary" :disabled="saving" @click="saveMidYear">Save mid-year review</button>
            </div>
          </div>

          <!-- End-year -->
          <div v-show="tab==='endyear'" class="panel">
            <div v-for="t in targets" :key="'e'+t.id" class="tgt">
              <strong>{{ t.kra || ('Target '+t.id) }}</strong>
              <div class="g2">
                <label class="full">Self-assessment / progress <textarea v-model="t.end_progress" rows="2"></textarea></label>
                <label class="full">Actual achievement + evidence notes <textarea v-model="t.end_actual_achievement" rows="2"></textarea></label>
                <label>Appraisee rating (1-5) <input type="number" min="1" max="5" step="0.1" v-model.number="t.end_emp_rating"></label>
                <label class="full">Challenges <textarea v-model="t.end_challenges" rows="2"></textarea></label>
                <label class="full">Employee comment <textarea v-model="t.end_emp_comment" rows="2"></textarea></label>
              </div>
              <div class="scores-readonly" v-if="t.end_sup_rating != null || t.end_agreed_rating != null">
                Appraiser: {{ t.end_sup_rating ?? '—' }} · Agreed: {{ t.end_agreed_rating ?? '—' }}
              </div>
            </div>
            <label>Final employee comments <textarea v-model="empComments" rows="3"></textarea></label>
            <div class="actions">
              <button class="hr-btn hr-btn-primary" :disabled="saving" @click="submitEndSelf">Submit self-assessment</button>
              <button class="hr-btn hr-btn-outline-primary" :disabled="saving" @click="confirmEmp">Confirm discussion took place</button>
              <button class="hr-btn hr-btn-outline-danger" @click="raiseDispute">Dispute final rating</button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl, toast } from "@/api";

function blankTarget() {
  return {
    kra: "", objective: "", activities: "", indicator: "", annual_target: "",
    completion_date: "", weight: 0, means_of_verification: "", emp_comments: "", sup_comments: ""
  };
}

export default {
  name: "PasMyAppraisal",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      saving: false,
      list: [],
      selectedId: null,
      detail: null,
      targets: [],
      tab: "targets",
      tabs: [
        { id: "targets", label: "Targets" },
        { id: "progress", label: "Progress" },
        { id: "midyear", label: "Mid-year" },
        { id: "endyear", label: "End-year" }
      ],
      progressForms: {},
      empComments: ""
    };
  },
  computed: {
    weightTotal() {
      return Math.round(this.targets.reduce((s, t) => s + (Number(t.weight) || 0), 0) * 100) / 100;
    },
    canEditTargets() {
      const st = this.detail?.assignment?.status || "";
      return ["not_started", "target_setting_in_progress", "targets_rejected"].includes(st);
    },
    formTemplate() {
      return this.detail?.form_template || null;
    }
  },
  mounted() { this.loadList(); },
  methods: {
    formatStatus(s) { return String(s || "").replace(/_/g, " "); },
    addTarget() { this.targets.push(blankTarget()); },
    async loadList() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "pasGetMyAppraisals");
        const res = await execute(fd, authUrl);
        this.list = res?.data?.data?.assignments || [];
      } finally { this.loading = false; }
    },
    async open(id) {
      this.selectedId = id;
      this.tab = "targets";
      const fd = new FormData();
      fd.append("function", "pasGetAssignment");
      fd.append("id", String(id));
      const res = await execute(fd, authUrl);
      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Failed to load");
        return;
      }
      this.detail = res.data.data;
      this.targets = (this.detail.targets || []).map((t) => ({
        ...t,
        completion_date: (t.completion_date || "").slice(0, 10),
        mid_revised_timeline: (t.mid_revised_timeline || "").slice(0, 10)
      }));
      if (!this.targets.length) this.targets = [blankTarget(), blankTarget()];
      const pf = {};
      this.targets.forEach((t) => {
        if (t.id) pf[t.id] = { progress_note: "", actual_achievement: "", challenges: "", support_required: "" };
      });
      this.progressForms = pf;
      this.empComments = this.detail.assignment?.emp_comments || "";
    },
    async saveTargets(asDraft) {
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "pasSaveTargets");
        fd.append("assignment_id", String(this.selectedId));
        fd.append("as_draft", asDraft ? "1" : "0");
        fd.append("targets", JSON.stringify(this.targets));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Targets saved");
          await this.open(this.selectedId);
        } else toast.error(res?.data?.message || "Save failed");
      } finally { this.saving = false; }
    },
    async submitTargets() {
      if (Math.abs(this.weightTotal - 100) > 0.05) {
        toast.error("Weight must total 100%");
        return;
      }
      this.saving = true;
      try {
        const fdSave = new FormData();
        fdSave.append("function", "pasSaveTargets");
        fdSave.append("assignment_id", String(this.selectedId));
        fdSave.append("as_draft", "0");
        fdSave.append("targets", JSON.stringify(this.targets));
        const saveRes = await execute(fdSave, authUrl);
        if (!saveRes?.data?.success) {
          toast.error(saveRes?.data?.message || "Save failed");
          return;
        }
        const fd = new FormData();
        fd.append("function", "pasSubmitTargets");
        fd.append("assignment_id", String(this.selectedId));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Submitted for supervisor review");
          await this.open(this.selectedId);
        } else toast.error(res?.data?.message || "Submit failed");
      } finally { this.saving = false; }
    },
    async saveProgress(targetId) {
      const p = this.progressForms[targetId] || {};
      const fd = new FormData();
      fd.append("function", "pasSaveTargetProgress");
      fd.append("assignment_id", String(this.selectedId));
      fd.append("target_id", String(targetId));
      Object.keys(p).forEach((k) => fd.append(k, p[k] || ""));
      const res = await execute(fd, authUrl);
      if (res?.data?.success) toast.success("Progress saved");
      else toast.error(res?.data?.message || "Failed");
    },
    async saveMidYear() {
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "pasSaveMidYearReview");
        fd.append("assignment_id", String(this.selectedId));
        fd.append("targets", JSON.stringify(this.targets));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Mid-year saved");
          await this.open(this.selectedId);
        } else toast.error(res?.data?.message || "Failed");
      } finally { this.saving = false; }
    },
    async submitEndSelf() {
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "pasSaveEndYearSelf");
        fd.append("assignment_id", String(this.selectedId));
        fd.append("emp_comments", this.empComments);
        fd.append("targets", JSON.stringify(this.targets));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Self-assessment submitted to supervisor");
          await this.open(this.selectedId);
        } else toast.error(res?.data?.message || "Failed");
      } finally { this.saving = false; }
    },
    async confirmEmp() {
      const fd = new FormData();
      fd.append("function", "pasConfirmAppraisal");
      fd.append("assignment_id", String(this.selectedId));
      fd.append("role", "employee");
      fd.append("note", "Discussion confirmed");
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        toast.success("Confirmation recorded");
        await this.open(this.selectedId);
      } else toast.error(res?.data?.message || "Failed");
    },
    async raiseDispute() {
      const reason = window.prompt("Reason for disagreement:");
      if (!reason) return;
      const fd = new FormData();
      fd.append("function", "pasRaiseDispute");
      fd.append("assignment_id", String(this.selectedId));
      fd.append("reason", reason);
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        toast.success("Dispute escalated");
        await this.open(this.selectedId);
      } else toast.error(res?.data?.message || "Failed");
    }
  }
};
</script>

<style scoped>
.cards { display:grid; grid-template-columns:repeat(auto-fill,minmax(260px,1fr)); gap:1rem; }
.card { text-align:left; border:1px solid #e2e8f0; border-radius:12px; background:#fff; padding:1rem; display:flex; flex-direction:column; gap:.35rem; cursor:pointer; }
.card:hover { border-color:#0c4a6e; }
.st { text-transform:capitalize; color:#0369a1; font-style:normal; font-weight:700; font-size:.8rem; }
.empty, .pad { padding:2rem; color:#64748b; }
.ws-head { display:flex; flex-wrap:wrap; gap:.75rem; align-items:flex-start; margin-bottom:1rem; }
.ws-head h3 { margin:0; font-size:1.05rem; }
.ws-head p { margin:.2rem 0 0; font-size:.85rem; color:#64748b; }
.tabs { display:flex; gap:.35rem; flex-wrap:wrap; margin-left:auto; }
.tabs button { border:1px solid #e2e8f0; background:#f8fafc; border-radius:8px; padding:.4rem .7rem; cursor:pointer; font-weight:600; font-size:.8rem; }
.tabs button.active { background:#0c4a6e; color:#fff; border-color:#0c4a6e; }
.panel { background:#fff; border:1px solid #e2e8f0; border-radius:14px; padding:1rem; }
.flow-box, .form-box { background:#f0f9ff; border:1px solid #bae6fd; border-radius:10px; padding:.75rem .9rem; margin-bottom:.85rem; font-size:.85rem; color:#0c4a6e; }
.form-box { background:#f8fafc; border-color:#e2e8f0; color:#334155; }
.flow-box ol { margin:.4rem 0 0; padding-left:1.15rem; }
.flow-box .info { color:#b45309; font-weight:700; margin:.5rem 0 0; }
.flow-box .ok { color:#047857; font-weight:700; margin:.5rem 0 0; }
.flow-box .bad { color:#dc2626; font-weight:700; margin:.5rem 0 0; }
.bad { color:#dc2626; }
.tgt { border:1px solid #e2e8f0; border-radius:10px; padding:.85rem; margin-bottom:.85rem; background:#fafbfc; }
.tgt-h { display:flex; justify-content:space-between; font-weight:700; margin-bottom:.5rem; }
.g2 { display:grid; grid-template-columns:1fr 1fr; gap:.6rem; }
.g2 label, .panel > label, .tgt > label { display:flex; flex-direction:column; gap:.25rem; font-size:.75rem; font-weight:700; color:#475569; }
.full { grid-column:1/-1; }
input, textarea { border:1px solid #e2e8f0; border-radius:8px; padding:.45rem .6rem; background:#fff; font-size:.9rem; }
.actions { display:flex; flex-wrap:wrap; gap:.5rem; margin-top:.75rem; }
.link { border:none; background:none; color:#0284c7; cursor:pointer; font-weight:600; }
.link.danger { color:#dc2626; }
.scores-readonly { margin-top:.4rem; font-size:.8rem; color:#64748b; }
.hr-btn-ghost { background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:.4rem .7rem; cursor:pointer; }
.muted { color:#94a3b8; }
@media (max-width:720px) { .g2 { grid-template-columns:1fr; } }
</style>
